import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableRowProps,
  TablePagination,
  Checkbox,
  Button,
} from '@mui/material'
import { useRef, useState, useMemo, MouseEvent, useEffect } from 'react'
import { HeadCell, Order, TableHeader, getComparator } from './TableHeader'
import styles from './DataTable.module.scss'

interface DataTableProps {
  tableHeaders: HeadCell[]
  tableRows: TableRowProps[]
  isLoading: boolean
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>, row: any) => void
  selectable?: boolean
  isErrored?: boolean
  disableSorting?: boolean
  errorMessage?: string
  pagination?: boolean
  page?: number
  rowsPerPage?: number
  totalCount?: number
  initialOrder?: 'asc' | 'desc'
  initialOrderBy?: keyof TableRowProps
  onSortChange?: (order: Order, orderBy: keyof TableRowProps) => void
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  rowsPerPageOptions?: number[]
  enableCheckboxes?: boolean
  selectedRows?: string[]
  onRowSelect?: (selectedRows: string[]) => void
  onExportAll?: () => void
  onExportSelected?: () => void
  onClearSelection?: () => void
  isExporting?: boolean
  disabledCheckboxIds?: string[]
  showExportButtons?: boolean
  maxHeight?: string | number
  enableVerticalScroll?: boolean
}

export const DataTable = ({
  tableHeaders,
  tableRows,
  isLoading,
  onClick,
  selectable,
  isErrored = false,
  disableSorting,
  errorMessage = 'Error loading data...please try again',
  pagination = false,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  rowsPerPageOptions = [5, 10, 25],
  enableCheckboxes = false,
  selectedRows = [],
  onRowSelect = () => {},
  onExportAll,
  onExportSelected,
  onClearSelection,
  isExporting = false,
  initialOrder = 'asc',
  initialOrderBy,
  onSortChange,
  disabledCheckboxIds = [],
  showExportButtons = false,
  maxHeight = '60vh',
  enableVerticalScroll = false,
}: DataTableProps) => {
  // TODO: khanh to fix, ignoring console warning for now
  console.error = (message) => {
    if (
      typeof message === 'string' &&
      message.includes('<table> cannot appear as a descendant of <p>')
    ) {
      return
    }
  }

  const defaultKeyRef = useRef<keyof TableRowProps>('' as keyof TableRowProps)
  const [order, setOrder] = useState<Order>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof TableRowProps>(
    initialOrderBy || defaultKeyRef.current
  )

  // Update internal state when external props change
  useEffect(() => {
    if (initialOrderBy) {
      setOrderBy(initialOrderBy)
    }
    setOrder(initialOrder)
  }, [initialOrder, initialOrderBy])

  useMemo(() => {
    const rows = tableRows[0] && Object.keys(tableRows[0])
    if (rows?.length > 0 && !disableSorting) {
      defaultKeyRef.current = rows[0] as keyof TableRowProps
    }
    // Only set default orderBy if no initialOrderBy is provided and we don't have a current orderBy
    if (!initialOrderBy && !orderBy) {
      setOrderBy(defaultKeyRef.current)
    }
  }, [tableRows, disableSorting, initialOrderBy, orderBy])

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof TableRowProps
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    onSortChange && onSortChange(isAsc ? 'desc' : 'asc', property)
  }

  const sortedRows = useMemo(
    () =>
      tableRows
        ?.slice()
        ?.sort((a, b) => getComparator(order, orderBy)(a as any, b as any)),
    [order, orderBy, tableRows]
  )

  const handleRowSelect = (row: TableRowProps) => {
    if (disabledCheckboxIds.includes((row as any).id)) {
      return
    }
    const rowId = (row as any).id
    const selectedIndex = selectedRows.indexOf(rowId)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, rowId]
    } else {
      newSelected = selectedRows.filter((id) => id !== rowId)
    }

    onRowSelect && onRowSelect(newSelected)
  }

  const isSelected = (row: TableRowProps) => {
    const rowId = (row as any).id
    return selectedRows.includes(rowId)
  }

  const isCheckboxDisabled = (row: TableRowProps) => {
    return disabledCheckboxIds.includes((row as any).id)
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(onExportAll || onExportSelected || onClearSelection) &&
        showExportButtons && (
          <Box className={styles.exportButtonsContainer}>
            {selectedRows.length > 0 ? (
              <>
                {onExportSelected && (
                  <Button
                    variant="contained"
                    onClick={onExportSelected}
                    disabled={isExporting}
                    className={styles.exportButton}
                  >
                    {isExporting
                      ? `Exporting...`
                      : `Export (${selectedRows.length}) Record ${
                          selectedRows.length > 1 ? 's' : ''
                        }`}
                  </Button>
                )}
                {onClearSelection && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onClearSelection}
                    disabled={isExporting}
                    className={styles.clearSelectionButton}
                  >
                    Clear Selection
                  </Button>
                )}
              </>
            ) : (
              onExportAll && (
                <Button
                  variant="contained"
                  onClick={onExportAll}
                  disabled={isExporting}
                  className={styles.exportButtonFullWidth}
                >
                  {isExporting ? `Exporting...` : 'Export All'}
                </Button>
              )
            )}
          </Box>
        )}
      <TableContainer
        style={{
          maxHeight: enableVerticalScroll ? maxHeight : '100%',
          overflow: enableVerticalScroll ? 'scroll' : 'visible',
        }}
        sx={{
          borderRadius: '8px',
          marginTop: 0,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          '& .MuiTableContainer-root': {
            borderRadius: '8px',
            marginTop: 0,
            boxShadow:
              '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
            position: 'relative',
          },
          '& .MuiTable-root': {
            minWidth: '650px',
          },
          '& .MuiTableHead-root': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#ffffff',
            minHeight: '56px',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: 'inherit',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            opacity: 1,
            padding: '12px 12px',
            borderBottom: '2px solid',
            borderColor: '#e0e0e0',
            color: '#333333',
            fontSize: '0.875rem',
            minHeight: '56px',
          },
          '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
            borderTopLeftRadius: '8px',
          },
          '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
            borderTopRightRadius: '8px',
          },
          '& .MuiTableHead-root .MuiTableCell-root:nth-of-type(2):first-of-type':
            {
              borderTopLeftRadius: '8px',
            },
          '& .MuiTableBody-root .MuiTableCell-root': {
            padding: '4px 12px',
          },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root:first-of-type':
            {
              borderBottomLeftRadius: '8px',
            },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root:last-of-type':
            {
              borderBottomRightRadius: '8px',
            },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root:nth-of-type(2)':
            {
              borderBottomLeftRadius: '8px',
            },
          '& .MuiTableRow-root': {
            height: 'auto',
            minHeight: '40px',
          },
          '& .MuiCircularProgress-root': {
            width: '40px',
            height: '40px',
          },
        }}
      >
        <Table padding="normal">
          <TableHeader
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            tableHeaders={tableHeaders}
            disableSorting={disableSorting}
            enableCheckboxes={enableCheckboxes}
            numSelected={selectedRows.length}
            rowCount={tableRows.length}
          />
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  align="center"
                  className={styles.loadingCell}
                  colSpan={tableHeaders.length}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : isErrored ? (
              <TableRow>
                <TableCell
                  className={styles.errorCell}
                  colSpan={tableHeaders.length}
                >
                  {errorMessage}
                </TableCell>
              </TableRow>
            ) : sortedRows.length > 0 ? (
              sortedRows.map((row, index) => {
                const isItemSelected = isSelected(row as TableRowProps)
                const rowData = row as TableRowProps
                return (
                  <TableRow
                    onClick={(event) => onClick && onClick(event, rowData)}
                    hover
                    tabIndex={-1}
                    key={rowData.id}
                    data-index={index}
                    className={
                      index % 2 === 0
                        ? `${styles.evenRow} ${
                            selectable
                              ? styles.selectableRow
                              : styles.defaultRow
                          }`
                        : `${styles.oddRow} ${
                            selectable
                              ? styles.selectableRow
                              : styles.defaultRow
                          }`
                    }
                    style={{ cursor: selectable ? 'pointer' : 'default' }}
                  >
                    {enableCheckboxes && !isCheckboxDisabled(rowData) && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(event) => {
                            event.stopPropagation()
                            handleRowSelect(rowData)
                          }}
                        />
                      </TableCell>
                    )}
                    {enableCheckboxes && isCheckboxDisabled(rowData) && (
                      <TableCell padding="checkbox" />
                    )}
                    {tableHeaders.map((header, cellIndex) =>
                      header.hidden ? null : (
                        <TableCell
                          align="left"
                          key={cellIndex}
                          className={styles.tableCell}
                        >
                          {(rowData as any)[header.id as string]}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  className={styles.noDataCell}
                  colSpan={tableHeaders.length}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination below the scrollable table but still inside DataTable component */}
      {pagination && (
        <Box
          sx={{
            mt: 0.5,
            backgroundColor: '#f5f5f5',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
            sx={{
              '& .MuiTablePagination-toolbar': {
                padding: '4px 16px',
                minHeight: '40px',
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}
